import { createContext, useState, useContext, useEffect } from "react";
import { defaultConfig } from "../data/siteConfig";

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext();

const SITE_ORIGIN = "https://www.happydayflowers.com.au";

// Every page that has its own <PageRouter> branch — and therefore its own URL.
const KNOWN_PAGES = new Set([
  "home", "shop", "gifts", "about", "delivery", "contact",
  "why-choose-us", "our-studio", "gift-messages", "custom-orders",
  "delivery-zones", "faqs",
]);

// Per-page <title> + meta description so each URL is a distinct, indexable page.
const PAGE_META = {
  home: {
    title: "Happy Day Flowers | Same-Day Flower Delivery Melbourne | Travancore Florist",
    description:
      "Happy Day Flowers — Melbourne's boutique family florist in Travancore. Handcrafted bouquets, same-day delivery across Melbourne. Order before 11am.",
  },
  shop: {
    title: "Shop Fresh Flowers & Bouquets | Happy Day Flowers Melbourne",
    description:
      "Browse handcrafted bouquets and arrangements for every occasion — birthdays, anniversaries, sympathy and more. Same-day flower delivery across Melbourne.",
  },
  gifts: {
    title: "Flowers with Gifts — Chocolates, Balloons & More | Happy Day Flowers",
    description:
      "Add something special to your flowers — chocolates, candles, teddy bears, balloons and gift boxes, delivered same-day across Melbourne.",
  },
  about: {
    title: "About Us — Boutique Family Florist in Travancore | Happy Day Flowers",
    description:
      "Meet Happy Day Flowers, a boutique family florist in Travancore, Melbourne, crafting fresh arrangements with locally sourced Victorian blooms.",
  },
  delivery: {
    title: "Same-Day Flower Delivery Across Melbourne | Happy Day Flowers",
    description:
      "Same-day flower delivery across Melbourne — order before 11am. See our delivery areas, times and fees.",
  },
  contact: {
    title: "Contact Us | Happy Day Flowers Melbourne",
    description:
      "Get in touch with Happy Day Flowers in Travancore, Melbourne. Call, email or visit our studio for custom orders and enquiries.",
  },
  "why-choose-us": {
    title: "Why Choose Us | Happy Day Flowers Melbourne",
    description:
      "Locally sourced Victorian blooms, handcrafted daily, same-day delivery and a 5-star reputation — why Melbourne chooses Happy Day Flowers.",
  },
  "our-studio": {
    title: "Our Studio in Travancore | Happy Day Flowers Melbourne",
    description:
      "Take a look inside the Happy Day Flowers studio in Travancore, Melbourne, where every arrangement is handcrafted fresh.",
  },
  "gift-messages": {
    title: "Gift Message Ideas for Flowers | Happy Day Flowers",
    description:
      "Not sure what to write? Browse gift message ideas for birthdays, anniversaries, sympathy and every occasion.",
  },
  "custom-orders": {
    title: "Custom Flower Orders & Events | Happy Day Flowers Melbourne",
    description:
      "Request a custom bouquet or arrangement for weddings, events and special occasions across Melbourne.",
  },
  "delivery-zones": {
    title: "Delivery Zones & Fees | Happy Day Flowers Melbourne",
    description:
      "Check whether we deliver to your Melbourne suburb, with delivery zones, fees and same-day cut-off times.",
  },
  faqs: {
    title: "Frequently Asked Questions | Happy Day Flowers",
    description:
      "Answers to common questions about ordering, delivery, payments and gift messages at Happy Day Flowers.",
  },
};

// page (+ optional shop category) → URL path
function pathForPage(page, filter) {
  if (page === "home") return "/";
  let path = `/${page}`;
  if (page === "shop" && filter && filter !== "all") {
    path += `?category=${encodeURIComponent(filter)}`;
  }
  return path;
}

// current URL → { page, filter }
function pageFromLocation() {
  const seg = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
  const page = seg && KNOWN_PAGES.has(seg) ? seg : "home";
  const params = new URLSearchParams(window.location.search);
  const filter = page === "shop" ? params.get("category") || "all" : "all";
  return { page, filter };
}

export function AppProvider({ children }) {
  const initial = pageFromLocation();
  const [page, setPage] = useState(initial.page);
  const [filter, setFilter] = useState(initial.filter);
  const [typeFilter, setTypeFilter] = useState(null);
  const [modalProduct, setModalProduct] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [toast, setToast] = useState(null);
  const [giftPickerCategory, setGiftPickerCategory] = useState(null);

  // Anchor the first history entry to the current URL so the first Back press
  // restores the right page (or exits cleanly from home).
  useEffect(() => {
    const here = pageFromLocation();
    window.history.replaceState({ page: here.page, filter: here.filter }, "");
    const onPop = () => {
      const s = window.history.state;
      const derived = pageFromLocation();
      const p = s?.page && KNOWN_PAGES.has(s.page) ? s.page : derived.page;
      const f = s?.filter ?? derived.filter;
      setPage(p);
      setFilter(f);
      setTypeFilter(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Keep the document title, description and canonical URL in sync with the
  // current page so every URL is its own indexable page for search engines.
  useEffect(() => {
    const meta = PAGE_META[page] || PAGE_META.home;
    document.title = meta.title;
    const url = SITE_ORIGIN + pathForPage(page, filter);
    const set = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    set('meta[name="description"]', "content", meta.description);
    set('link[rel="canonical"]', "href", url);
    set('meta[property="og:url"]', "content", url);
    set('meta[property="og:title"]', "content", meta.title);
    set('meta[property="og:description"]', "content", meta.description);
  }, [page, filter]);

  const navigate = (newPage, newFilter = "all") => {
    window.history.pushState(
      { page: newPage, filter: newFilter },
      "",
      pathForPage(newPage, newFilter),
    );
    setPage(newPage);
    setFilter(newFilter);
    setTypeFilter(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showToast = (data) => {
    setToast(typeof data === "string" ? { name: data } : data);
    setTimeout(() => setToast(null), 4300);
  };

  const clearToast = () => setToast(null);

  return (
    <AppContext.Provider
      value={{
        page,
        navigate,
        filter,
        setFilter,
        typeFilter,
        setTypeFilter,
        modalProduct,
        setModalProduct,
        checkoutOpen,
        setCheckoutOpen,
        mobileMenuOpen,
        setMobileMenuOpen,
        config,
        setConfig,
        toast,
        showToast,
        clearToast,
        giftPickerCategory,
        setGiftPickerCategory,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useApp = () => useContext(AppContext);
